import React, { useRef, useState } from "react";
import Webcam from "react-webcam";
import html2canvas from "html2canvas";
import { Camera, Loader2 } from "lucide-react";
import { GoogleMap, OverlayView, useJsApiLoader } from "@react-google-maps/api";
import mohannagar from "../Images/mohannagar.png";
import gpslogo from "../Images/gpslogo.png";

const GOOGLE_MAPS_API_KEY = "AIzaSyDVUbdHE7cVJYbA9SXThZuSwYo2mxZyHEk";

const FIXED_LOCATION = {
  lat: 28.6875765,
  lon: 77.4511482,
  address:
    "Yashoda Hospital, Sanjay Nagar, B-1 & 2, Duplex Flats, Block P, Sector 23, Sanjay Nagar, Ghaziabad, Uttar Pradesh 201002, India",
};

const GpsCamera = () => {
  const webcamRef = useRef(null);
  const captureRef = useRef(null);

  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [askName, setAskName] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const capture = () => {
    setAskName(true);
    const img = webcamRef.current.getScreenshot();
    setImageSrc(img);
  };

  const confirmAndSave = async () => {
    setAskName(false);
    setLoading(true);

    setTimeout(async () => {
      const canvas = await html2canvas(captureRef.current, {
        scale: 2,
        useCORS: true,
      });

      const finalImg = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = finalImg;
      link.download = `${name || "gps_photo"}.png`;
      link.click();

      // RESET
      setName("");
      setImageSrc(null);
      setLoading(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black to-zinc-900 flex items-center justify-center p-4">
      <div className="bg-black rounded-[2.5rem] p-3 md:p-6 shadow-[0_0_60px_rgba(255,255,255,0.15)] w-full max-w-sm md:max-w-6xl">

        <div className="flex flex-col md:flex-row gap-6">

          {/* CAMERA */}
          <div className="flex-1">
            <div className="relative rounded-2xl overflow-hidden shadow-xl mb-4 border border-white/20">
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/png"
                videoConstraints={{ facingMode: "environment" }}
                className="w-full aspect-[9/16] md:aspect-[9/14] object-cover"
              />
            </div>

            <button
              onClick={capture}
              className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3 rounded-xl flex justify-center items-center gap-2"
            >
              <Camera /> CAPTURE
            </button>
          </div>

          {/* PREVIEW */}
          <div className="flex-1">
            {imageSrc ? (
              <div
                ref={captureRef}
                className="relative w-full aspect-[9/16] md:aspect-[9/14] rounded-xl overflow-hidden bg-black"
              >
                <img src={imageSrc} className="w-full h-full object-cover" />

                {!askName && name && (
                  <div className="absolute top-3 left-3 bg-black/70 text-white text-xs md:text-sm px-3 py-1 rounded">
                    {name}
                  </div>
                )}

                {askName && (
                  <div className="absolute top-3 left-3 bg-white px-2 py-1 rounded flex gap-1">
                    <input
                      className="text-xs md:text-sm outline-none"
                      placeholder="Enter Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <button
                      onClick={confirmAndSave}
                      className="text-xs font-bold text-green-600"
                    >
                      ✓
                    </button>
                  </div>
                )}

                {/* GPS LOGO */}
                <img
                  src={gpslogo}
                  className="absolute bottom-[111px] md:bottom-[138px] right-[6px] w-10 md:w-14"
                />

                {/* MAP + INFO */}
                
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-2 md:p-3 text-white text-[10px] md:text-xs">
                  <div className="flex gap-2">

                    {isLoaded && (
                     <div className="w-20 h-20 md:w-28 md:h-28 border border-white rounded overflow-hidden">
                        <GoogleMap
                          center={{
                            lat: FIXED_LOCATION.lat,
                            lng: FIXED_LOCATION.lon,
                          }}
                          zoom={18}
                          mapContainerStyle={{ width: "100%", height: "100%" }}
                          options={{
                            disableDefaultUI: true,
                            gestureHandling: "none",
                            mapTypeId: "hybrid", // 🌍 SAME AS YOUR LINK
                          }}
                        >
                          <OverlayView
                            position={{
                              lat: FIXED_LOCATION.lat,
                              lng: FIXED_LOCATION.lon,
                            }}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                          >
                            <img src={mohannagar} className="w-6" />
                          </OverlayView>
                        </GoogleMap>
                      </div>
                    )}

                    <div className="flex-1 leading-tight">
                    
                      <div className="font-semibold font-lg">
                        Ghaziabad,Uttar Pradesh,🇮🇳
                      </div>
                      <div className="opacity-90 mt-[2px] font-lg">
                        {FIXED_LOCATION.address}
                      </div>
                      <div className=" mt-[2px] font-lg">
                        Lat {FIXED_LOCATION.lat} | Long {FIXED_LOCATION.lon}
                      </div>
                      <div className="mt-[2px] font-lg">
                        {new Date().toLocaleString("en-IN", {
                          weekday: "long",
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true,
                          })} GMT +05:30

                      </div>
                    </div>

                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-white/40 text-sm">
                Captured photo will appear here
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div className="text-white mt-4 flex gap-2 items-center justify-center">
            <Loader2 className="animate-spin" /> Saving...
          </div>
        )}
      </div>
    </div>
  );
};

export default GpsCamera;
